'use client';

import { type ReactNode, useEffect, useRef } from 'react';

import { useUserResources } from '@/presenters/hooks/user';

import { useRichRelevancePlacements } from '../../hooks/use-rich-relevance-placements';
import { useGlobalStore } from '../global';

import {
  Provider,
  createModularContentStore,
  type ModularContentStoreApi,
} from './context';

export interface ModularContentContextProviderProps {
  id?: string;
  name: string;
  children: ReactNode;
  placements: string[];
}

const ModularContentContextProvider = ({
  id,
  name,
  children,
  placements,
}: ModularContentContextProviderProps) => {
  const storeRef = useRef<ModularContentStoreApi>();

  if (!storeRef.current) {
    storeRef.current = createModularContentStore({
      id,
      name,
      recommendations: {
        isLoading: false,
        isError: false,
      },
    });
  }

  const region = useGlobalStore((state) => state.session.region);

  const { data: userId } = useUserResources((data) => data.id);
  const { data: userTrackingId } = useUserResources((data) => data.trackingId);

  const { data, isLoading, isError } = useRichRelevancePlacements(
    placements,
    { region, userId, sessionId: userTrackingId },
    { categoryId: id }
  );

  useEffect(() => {
    if (!storeRef.current) return;

    const { setRecommendations } = storeRef.current.getState();

    setRecommendations({ isLoading, isError, data });
  }, [data, isLoading, isError]);

  return <Provider value={storeRef.current}>{children}</Provider>;
};

export default ModularContentContextProvider;
